/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { AppState } from "Dataflows/Reducers";
import { IFilterValue } from "Interfaces/IFilterValue";
import { IVisualStudioBenefit } from "Interfaces/VisualStudioBenefits/IVisualStudioBenefit";

/**
 * Returns the list of visual studio benefits
 * in the current visual studio benefits state.
 * @param visualStudioBenefitState
 */
export const getVisualStudioBenefits = (state: AppState): IVisualStudioBenefit[] =>
  state.visualStudioBenefitState.visualStudioBenefits;

/**
 * Returns a boolean value for whether
 * or not the visual studio benefits are still loading.
 * @param visualStudioBenefitState
 */
export const isLoadingVisualStudioBenefits = (state: AppState): boolean =>
  state.visualStudioBenefitState.isLoadingVisualStudioBenefits;

/**
 * Returns an error message if there's an
 * issue loading the visual studio benefits.
 * @param visualStudioBenefitState
 */
export const getLoadVisualStudioBenefitsError = (state: AppState): string =>
  state.visualStudioBenefitState.loadVisualStudioBenefitsError;

/**
 * Returns the current page.
 * @param visualStudioBenefitState
 */
export const getCurrentPage = (state: AppState): number =>
  state.visualStudioBenefitState.currentPage;

/**
 * Returns the page size.
 * @param visualStudioBenefitState
 */
export const getPageSize = (state: AppState): number => state.visualStudioBenefitState.pageSize;

/**
 * Returns the search text.
 * @param visualStudioBenefitState
 */
export const getSearchText = (state: AppState): string => state.visualStudioBenefitState.searchText;

/**
 * Returns the filtered count.
 * @param visualStudioBenefitState
 */
export const getFilteredCount = (state: AppState): number =>
  state.visualStudioBenefitState.filteredCount;

/**
 * Returns the list of filter values for
 * visual studio benefits.
 * @param visualStudioBenefitState
 */
export const getVisualStudioBenefitsFilterValues = (state: AppState): IFilterValue[] =>
  state.visualStudioBenefitState.visualStudioBenefitsFilterValues;

/**
 * Returns the file download status.
 * @param state
 * @returns {string} file download status
 */
export const getFileDownloadStatus = (state: AppState): string =>
  state.visualStudioBenefitState.fileDownloadStatus;

/**
 * Returns the file download data.
 * @param state App state.
 * @returns {string} downloaded data
 */
export const getFileDownloadData = (state: AppState): string =>
  state.visualStudioBenefitState.fileDownloadData;
